import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DollarSign, FileText, Trophy, TrendingUp, MapPin, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth-store'
import { AddressAutocomplete } from '@/components/shared/address-autocomplete'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageLoader } from '@/components/ui/page-loader'
import { Badge } from '@/components/ui/badge'
import { CHART_COLORS } from '@/lib/chart-theme'
import { clientsService, ClientDetail, ClientProposal, ContactPayload } from '@/services/clients-service'
import type { ClientContact } from '@/types'

// ── Status helpers ────────────────────────────────────────────────────────

function proposalDisplayStatus(p: ClientProposal): string {
  if (p.signedAt) return 'Signed'
  if (p.expiresAt && new Date(p.expiresAt) < new Date() && !p.signedAt) return 'Expired'
  if (p.viewedAt) return 'Viewed'
  if (p.sentToClientAt) return 'Sent'
  return 'Draft'
}

const statusVariant = (status: string) => {
  switch (status) {
    case 'Signed':  return 'success'
    case 'Sent':    return 'info'
    case 'Viewed':  return 'warning'
    case 'Draft':   return 'secondary'
    case 'Expired': return 'destructive'
    default:        return 'secondary'
  }
}

// ── Stat Card ─────────────────────────────────────────────────────────────

function StatCard({ title, value, icon: Icon, iconColor }: {
  title: string; value: string; icon: React.ElementType; iconColor: string
}) {
  return (
    <Card className="hover:shadow-card-hover transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold tracking-tight">{value}</p>
          </div>
          <div className="rounded-xl p-2.5" style={{ backgroundColor: `${iconColor}14` }}>
            <Icon className="h-5 w-5" style={{ color: iconColor }} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000)     return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toLocaleString()}`
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

// ── Icons ─────────────────────────────────────────────────────────────────

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function XCircleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6" />
      <path d="M9 9l6 6" />
    </svg>
  )
}

// ── Contact Form Modal ────────────────────────────────────────────────────

function ContactModal({
  clientId,
  contact,
  onClose,
  onSave,
}: {
  clientId: string
  contact?: ClientContact
  onClose: () => void
  onSave: (c: ClientContact) => void
}) {
  const [firstName, setFirstName]   = useState(contact?.firstName || '')
  const [lastName,  setLastName]    = useState(contact?.lastName  || '')
  const [email,     setEmail]       = useState(contact?.email     || '')
  const [phone,     setPhone]       = useState(contact?.phone     || '')
  const [title,     setTitle]       = useState(contact?.title     || '')
  const [isPrimary, setIsPrimary]   = useState(contact?.isPrimary ?? false)
  const [saving,    setSaving]      = useState(false)
  const [error,     setError]       = useState('')

  async function handleSave() {
    if (!firstName.trim()) { setError('First name is required'); return }
    setSaving(true)
    setError('')
    try {
      const payload: ContactPayload = {
        firstName: firstName.trim(),
        lastName:  lastName.trim()  || undefined,
        email:     email.trim()     || undefined,
        phone:     phone.trim()     || undefined,
        title:     title.trim()     || undefined,
        isPrimary,
      }
      let saved: ClientContact
      if (contact) {
        saved = await clientsService.updateContact(clientId, contact.id, payload)
      } else {
        saved = await clientsService.createContact(clientId, payload)
      }
      onSave(saved)
      onClose()
    } catch (err) {
      setError('Failed to save contact. Please try again.')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="cust-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cust-modal">
        <div className="cust-modal-header">
          <h2 className="cust-modal-title">{contact ? 'Edit Contact' : 'Add Contact'}</h2>
          <button className="cust-modal-close" onClick={onClose}><XIcon className="h-4 w-4" /></button>
        </div>
        <div className="cust-modal-body">
          <div className="cust-modal-row">
            <div className="cust-modal-field" style={{ flex: 1 }}>
              <label className="cust-modal-label">First Name *</label>
              <input className="cust-modal-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
            </div>
            <div className="cust-modal-field" style={{ flex: 1 }}>
              <label className="cust-modal-label">Last Name</label>
              <input className="cust-modal-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" />
            </div>
          </div>
          <div className="cust-modal-field">
            <label className="cust-modal-label">Title / Role</label>
            <input className="cust-modal-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Owner, Project Manager, Site Supervisor" />
          </div>
          <div className="cust-modal-field">
            <label className="cust-modal-label">Phone</label>
            <input className="cust-modal-input" type="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" />
          </div>
          <div className="cust-modal-field">
            <label className="cust-modal-label">Email</label>
            <input className="cust-modal-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" />
          </div>
          <div className="cust-modal-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="contact-primary"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: 'pointer' }}
            />
            <label htmlFor="contact-primary" className="cust-modal-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
              Primary contact
            </label>
          </div>
          {error && <p style={{ fontSize: 13, color: 'var(--error, #e53e3e)', marginTop: 4 }}>{error}</p>}
        </div>
        <div className="cust-modal-footer">
          <button className="cust-modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="cust-modal-btn-save" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Edit Customer Modal ───────────────────────────────────────────────────

function EditCustomerModal({
  client,
  onClose,
  onSave,
  onDelete,
  isAdmin,
}: {
  client: { id: string; name: string; address?: string; city?: string; state?: string; zip?: string; isResidence?: boolean }
  onClose: () => void
  onSave: (updated: any) => void
  onDelete: () => Promise<void>
  isAdmin: boolean
}) {
  const [name,       setName]       = useState(client.name       || '')
  const [address,    setAddress]    = useState(client.address    || '')
  const [city,       setCity]       = useState(client.city       || '')
  const [stateVal,   setStateVal]   = useState(client.state      || '')
  const [zip,        setZip]        = useState(client.zip        || '')
  const [isResidence, setIsResidence] = useState(client.isResidence ?? false)
  const [saving,     setSaving]     = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting,   setDeleting]   = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      const updated = await clientsService.update(client.id, {
        name:        name        || undefined,
        address:     address     || undefined,
        city:        city        || undefined,
        state:       stateVal    || undefined,
        zip:         zip         || undefined,
        isResidence,
      })
      onSave(updated)
      onClose()
    } catch (err) {
      console.error('Failed to update customer', err)
    } finally {
      setSaving(false)
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true)
    try {
      await onDelete()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="cust-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cust-modal">
        <div className="cust-modal-header">
          <h2 className="cust-modal-title">Edit Customer</h2>
          <button className="cust-modal-close" onClick={onClose}><XIcon className="h-4 w-4" /></button>
        </div>

        {confirmingDelete ? (
          /* ── Confirm delete view ── */
          <div className="cust-modal-body">
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--red-50, #fef2f2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Trash2 size={20} style={{ color: 'var(--red, #ef4444)' }} />
              </div>
              <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Delete {client.name}?</p>
              <p style={{ fontSize: 13, color: 'var(--s500)', lineHeight: 1.5 }}>
                This will permanently remove the customer and all their contacts. This action cannot be undone.
              </p>
            </div>
          </div>
        ) : (
          /* ── Edit form ── */
          <div className="cust-modal-body">
            <div className="cust-modal-field">
              <label className="cust-modal-label">Customer / Company Name *</label>
              <input className="cust-modal-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Riverside Property Management" />
            </div>
            <div className="cust-modal-field">
              <label className="cust-modal-label">Street Address</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(street, c, s, z) => { setAddress(street); setCity(c); setStateVal(s); setZip(z) }}
                placeholder="Start typing an address..."
                className="cust-modal-input"
              />
            </div>
            <div className="cust-modal-row">
              <div className="cust-modal-field" style={{ flex: 2 }}>
                <label className="cust-modal-label">City</label>
                <input className="cust-modal-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
              </div>
              <div className="cust-modal-field" style={{ flex: 1 }}>
                <label className="cust-modal-label">State</label>
                <input className="cust-modal-input" value={stateVal} onChange={(e) => setStateVal(e.target.value)} placeholder="CA" />
              </div>
              <div className="cust-modal-field" style={{ flex: 1 }}>
                <label className="cust-modal-label">Zip</label>
                <input className="cust-modal-input" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="90210" />
              </div>
            </div>
            <div className="cust-modal-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="edit-cust-residence"
                checked={isResidence}
                onChange={(e) => setIsResidence(e.target.checked)}
                style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: 'pointer' }}
              />
              <label htmlFor="edit-cust-residence" className="cust-modal-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                Residential customer
              </label>
            </div>
          </div>
        )}

        <div className="cust-modal-footer">
          {/* Delete button — bottom left, admin only */}
          {isAdmin && !confirmingDelete && (
            <button
              className="cust-modal-btn-delete"
              onClick={() => setConfirmingDelete(true)}
              disabled={saving}
            >
              <Trash2 size={14} />
              Delete Customer
            </button>
          )}

          {/* Spacer pushes cancel+save to the right when no delete btn */}
          <div style={{ flex: 1 }} />

          {confirmingDelete ? (
            <>
              <button className="cust-modal-btn-cancel" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="cust-modal-btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </>
          ) : (
            <>
              <button className="cust-modal-btn-cancel" onClick={onClose}>Cancel</button>
              <button className="cust-modal-btn-save" disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Contact Card ──────────────────────────────────────────────────────────

function ContactCard({
  contact,
  clientId,
  isLast,
  onEdit,
  onDelete,
}: {
  contact: ClientContact
  clientId: string
  isLast?: boolean
  onEdit: (c: ClientContact) => void
  onDelete: (id: string) => void
}) {
  const [deleting, setDeleting] = useState(false)
  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  async function handleDelete() {
    if (!confirm(`Remove ${fullName} as a contact?`)) return
    setDeleting(true)
    try {
      await clientsService.deleteContact(clientId, contact.id)
      onDelete(contact.id)
    } catch (err) {
      console.error(err)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div style={{
      padding: '14px 24px',
      borderBottom: isLast ? 'none' : '1px solid var(--s100, #f1f3f5)',
    }}>
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--s900)' }}>{fullName}</span>
          {contact.isPrimary && (
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
              padding: '1px 7px', borderRadius: 20,
              background: 'rgba(var(--primary-rgb, 0,116,244), 0.1)',
              color: 'var(--primary)',
            }}>Primary</span>
          )}
          {/* Actions inline with name */}
          <div style={{ display: 'flex', gap: 2, marginLeft: 2 }}>
            <button
              onClick={() => onEdit(contact)}
              title="Edit contact"
              style={{ padding: '3px', borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--s400)', display: 'flex', alignItems: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--s400)')}
            >
              <PencilIcon className="h-3 w-3" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Remove contact"
              style={{ padding: '3px', borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--s400)', display: 'flex', alignItems: 'center' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#e53e3e')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--s400)')}
            >
              <XCircleIcon />
            </button>
          </div>
        </div>
        {contact.title && (
          <div style={{ fontSize: 12, color: 'var(--s500)', marginBottom: 4 }}>{contact.title}</div>
        )}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {contact.phone && (
            <a href={`tel:${contact.phone}`} style={{ fontSize: 13, color: 'var(--s700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 6.08 6.08l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              {contact.phone}
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} style={{ fontSize: 13, color: 'var(--s700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
              {contact.email}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN' || user?.role?.toUpperCase() === 'SUPERADMIN'
  const [detail,    setDetail]    = useState<ClientDetail | null>(null)
  const [contacts,  setContacts]  = useState<ClientContact[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [showEdit,  setShowEdit]  = useState(false)
  const [showAddContact,  setShowAddContact]  = useState(false)
  const [editingContact,  setEditingContact]  = useState<ClientContact | undefined>()

  useEffect(() => {
    if (!id) return
    Promise.all([
      clientsService.get(id),
      clientsService.listContacts(id),
    ])
      .then(([d, c]) => { setDetail(d); setContacts(c) })
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [id])

  if (isLoading) {
    return (
      <PageLoader />
    )
  }

  if (!detail) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ fontWeight: 500, color: 'var(--s500)' }}>Customer not found</p>
        <button className="btn" style={{ marginTop: '16px' }} onClick={() => navigate('/customers')}>
          Back to Customers
        </button>
      </div>
    )
  }

  const { client, stats, proposals } = detail
  const addressParts = [client.address, client.city, client.state].filter(Boolean)
  const fullAddress = addressParts.length > 0
    ? `${addressParts.join(', ')}${client.zip ? ` ${client.zip}` : ''}`
    : null

  function handleEditSave(updated: any) {
    if (!detail) return
    setDetail({ ...detail, client: { ...detail.client, ...updated } })
  }

  function handleContactSaved(saved: ClientContact) {
    setContacts((prev) => {
      const exists = prev.find((c) => c.id === saved.id)
      if (exists) {
        // If the saved contact is now primary, demote others in UI
        let updated = prev.map((c) => c.id === saved.id ? saved : (saved.isPrimary ? { ...c, isPrimary: false } : c))
        return updated
      }
      // New contact — if primary, demote others
      let updated = saved.isPrimary ? prev.map((c) => ({ ...c, isPrimary: false })) : [...prev]
      return [...updated, saved].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
    })
  }

  function handleContactDeleted(contactId: string) {
    setContacts((prev) => prev.filter((c) => c.id !== contactId))
  }

  async function handleDeleteCustomer() {
    if (!id) return
    await clientsService.deleteClient(id)
    navigate('/customers')
  }

  return (
    <>
    <div className="space-y-6 animate-fade-in">

      {/* Header */}
      <div className="est-list-head">
        <div className="flex items-center gap-2">
          <h1 className="est-list-title">{client.name}</h1>
          {client.isResidence && (
            <span className="cust-residential-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
              </svg>
              Residential
            </span>
          )}
          <button className="est-edit-btn" title="Edit customer" onClick={() => setShowEdit(true)}>
            <PencilIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Address */}
      {fullAddress && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--s500)', fontSize: 13, marginTop: -8 }}>
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          {fullAddress}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Client Revenue"   value={formatCurrency(stats.totalRevenue)} icon={DollarSign} iconColor={CHART_COLORS.blue} />
        <StatCard title="Total Bids"       value={String(stats.totalBids)}            icon={FileText}   iconColor={CHART_COLORS.purple} />
        <StatCard title="Bids Won"         value={String(stats.bidsWon)}              icon={Trophy}     iconColor="#f1586c" />
        <StatCard title="Close Rate"       value={`${stats.closeRate}%`}              icon={TrendingUp} iconColor={CHART_COLORS.sky} />
      </div>

      {/* Contacts */}
      <Card>
        <CardHeader style={{ paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <CardTitle className="text-base">Contacts</CardTitle>
              <p className="text-sm text-muted-foreground" style={{ marginTop: 2 }}>People at this company</p>
            </div>
            <button
              className="btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, fontSize: 13, padding: '0 12px' }}
              onClick={() => { setEditingContact(undefined); setShowAddContact(true) }}
            >
              <PlusIcon /> Add Contact
            </button>
          </div>
        </CardHeader>
        <CardContent style={{ paddingTop: 0, paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
          {contacts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 24px', color: 'var(--s400)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 8px', opacity: 0.4 }}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <p style={{ fontSize: 13 }}>No contacts yet. Add the person who runs this job.</p>
            </div>
          ) : (
            <div style={{ paddingBottom: 4 }}>
              {contacts.map((c, i) => (
                <ContactCard
                  key={c.id}
                  contact={c}
                  clientId={client.id}
                  isLast={i === contacts.length - 1}
                  onEdit={(c) => { setEditingContact(c); setShowAddContact(true) }}
                  onDelete={handleContactDeleted}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proposals Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base">Proposals</CardTitle>
            <p className="text-sm text-muted-foreground">All proposals for this customer</p>
          </div>
          {proposals.length > 0 && <Badge variant="secondary">{proposals.length}</Badge>}
        </CardHeader>
        <CardContent className="pt-0">
          {proposals.length > 0 ? (
            <div className="divide-y divide-border border-y border-border">
              {proposals.map((p) => {
                const status = proposalDisplayStatus(p)
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between py-3 px-1 hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/proposals/${p.id}`, { state: { from: 'customer', customerId: id, customerName: client.name } })}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.proposalNumber && <span className="prop-id mr-2">{p.proposalNumber}</span>}
                        {formatShortDate(p.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-3">
                      <span className="text-sm font-semibold hidden sm:block">
                        ${Number(p.estimatedValue).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                      <Badge variant={statusVariant(status) as any}>{status}</Badge>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[140px] text-muted-foreground gap-2">
              <FileText className="h-8 w-8 opacity-30" />
              <span className="text-sm">No proposals yet for this customer</span>
            </div>
          )}
        </CardContent>
      </Card>

    </div>

    {showEdit && (
      <EditCustomerModal
        client={client}
        onClose={() => setShowEdit(false)}
        onSave={handleEditSave}
        onDelete={handleDeleteCustomer}
        isAdmin={isAdmin}
      />
    )}

    {showAddContact && (
      <ContactModal
        clientId={client.id}
        contact={editingContact}
        onClose={() => { setShowAddContact(false); setEditingContact(undefined) }}
        onSave={handleContactSaved}
      />
    )}
    </>
  )
}
